import { FormControl, Select, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useViewport } from 'contexts/viewport';
import { cloneElement, forwardRef, useImperativeHandle, useRef } from 'react';
import FormLabel from 'shell/components/FormLabel';

const CustomFormControl = styled(FormControl)(({ $renderValue }) => {
    return {
        width: $renderValue ? 'auto' : '100%',
    };
});

const CustomFormLabel = styled(FormLabel)(({ disabled, theme }) => {
    return {
        marginBottom: theme.spacing(1.25),
        color: theme.palette.ensightful.neutral[900],
        whiteSpace: 'initial',
        wordWrap: 'break-word',
        '&.Mui-focused': {
            color: 'initial',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.ensightful.error[600],
            ...(disabled && {
                color: theme.palette.ensightful.neutral[400],
            }),
        },
        ...(disabled && {
            color: `${theme.palette.ensightful.neutral[400]} !important`,
        }),
    };
});

const CustomSelect = styled(Select)(({ disabled, error, $fontStyles, $renderValue, $size, theme }) => {
    const height = {
        small: '28px',
        medium: '36px',
        large: '48px',
    }[$size];

    const padding = {
        small: '6px 12px',
        medium: '10px 12px',
        large: '14px 12px',
    }[$size];

    return {
        height,
        width: $renderValue ? 'auto' : '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        background: theme.palette.ensightful.shades[0],
        '&:focus-within': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
            outlineOffset: '2px',
        },
        '&:hover': {
            '& > fieldset': {
                borderColor: `${theme.palette.ensightful.primary[700]} !important`,
                ...(disabled && {
                    borderColor: theme.palette.ensightful.neutral[400],
                }),
                ...(error && {
                    borderColor: `${theme.palette.ensightful.error[700]} !important`,
                }),
            },
        },
        '&.Mui-focused': {
            '& > fieldset': {
                border: `1px solid ${theme.palette.ensightful.primary[500]} !important`,
                ...(disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
                ...(error && {
                    border: `1px solid ${theme.palette.ensightful.error[700]} !important`,
                }),
            },
        },
        ...(disabled && {
            borderColor: theme.palette.ensightful.neutral[400],
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
        }),
        ...(error && {
            borderColor: `${theme.palette.ensightful.error[700]} !important`,
        }),
        '& > div': {
            padding,
            ...$fontStyles,
        },
        '& > svg': {
            fontSize: '1.5rem',
            ...(disabled && {
                color: theme.palette.ensightful.neutral[500],
            }),
        },
        '& > fieldset': {
            border: $renderValue ? 'none' : `1px solid ${theme.palette.ensightful.neutral[500]}`,
            transition: theme.transitions.default,
            ...(disabled && {
                borderColor: theme.palette.ensightful.neutral[400],
            }),
            '& > legend': {
                width: 0,
            },
        },

        '& .MuiStack-root': {
            gap: '0.25rem',

            '& > p.MuiTypography-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        },
    };
});

/**
 * A customizable Select component that supports various sizes, labels, and error states.
 *
 * @param {object} props - The component props.
 * @param {object} [props.sx] - The system prop that allows defining system overrides as well as additional CSS styles.
 * @param {string} [props.className] - The CSS class name.
 * @param {boolean} [props.disabled] - If true, the select will be disabled.
 * @param {boolean} [props.error] - If true, the select will be in an error state.
 * @param {string} [props.label] - The label for the select.
 * @param {string} [props.labelId] - The ID for the label. Required if label is provided.
 * @param {boolean} [props.matchWidth] - If true, forces the menu to be the same width as the Select anchor.
 * @param {function} [props.renderValue] - Function to render the selected value.
 * @param {boolean} [props.required] - If true, the select will be required.
 * @param {'small'|'medium'|'large'} [props.size] - The size of the select.
 * @param {object} [rest] - Additional props passed to the underlying MUI Select component.
 * @param {React.Ref} ref - Ref forwarded to the select element.
 */
export default forwardRef(
    (
        { sx, className, disabled, error, label, labelId, matchWidth = false, renderValue, required, size, ...rest },
        ref
    ) => {
        if (label && !labelId) {
            throw new Error(
                'Shell: Missing labelId in component Select. You must provide a labelId when also providing a label.'
            );
        }

        const theme = useTheme();
        const { device } = useViewport();

        const selectRef = useRef(null);
        const formControlRef = useRef(null);

        const mappedSize =
            size ||
            {
                mobile: 'medium',
                tablet: 'medium',
                desktop: 'medium',
            }[device];

        const fontStyles = {
            small: {
                minHeight: '1.5rem',
                fontSize: `${theme.typography.p3.fontSize.small}rem`,
                fontWeight: theme.typography.p3.fontWeight.regular,
                letterSpacing: `${theme.typography.p3.letterSpacing.small}rem`,
            },
            medium: {
                minHeight: '1.625rem',
                fontSize: `${theme.typography.p2.fontSize.large}rem`,
                fontWeight: theme.typography.p2.fontWeight.regular,
                letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
            },
            large: {
                minHeight: '1.75rem',
                fontSize: `${theme.typography.p1.fontSize.large}rem`,
                fontWeight: theme.typography.p1.fontWeight.regular,
                letterSpacing: `${theme.typography.p1.letterSpacing.large}rem`,
            },
        }[mappedSize];

        useImperativeHandle(ref, () => selectRef.current);

        const SelectComponent = (
            <CustomSelect
                ref={selectRef}
                sx={label ? undefined : sx}
                className={label ? undefined : className}
                disabled={disabled}
                error={error}
                $fontStyles={fontStyles}
                MenuProps={{
                    sx: {
                        '& > div.MuiBackdrop-root': { background: 'transparent' },
                        '& > div.MuiPaper-root': {
                            borderRadius: '16px',
                            maxHeight: '168px',
                            boxShadow: theme.palette.ensightful.shadows[1],
                            overflow: 'hidden',
                            '& > ul.MuiList-root': {
                                maxHeight: '168px',
                                padding: theme.spacing(2),
                                overflowY: 'scroll',
                                '& > li.Mui-focusVisible': {
                                    outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
                                    outlineOffset: '-5px',
                                },
                            },
                        },
                    },
                    ...(matchWidth && {
                        TransitionProps: {
                            onEntering: (node) => {
                                if (selectRef.current) node.style.width = `${selectRef.current.clientWidth}px`;
                            },
                        },
                    }),
                }}
                $renderValue={renderValue}
                $size={mappedSize}
                {...rest}
            />
        );

        return label ? (
            <CustomFormControl ref={formControlRef} sx={sx} className={className} $renderValue={renderValue}>
                <CustomFormLabel id={labelId} color={'primary'} $disabled={disabled} required={required}>
                    {label}
                </CustomFormLabel>
                {cloneElement(SelectComponent, { label, labelId })}
            </CustomFormControl>
        ) : (
            SelectComponent
        );
    }
);
